// Shelf — turns the "From Our Home to Yours" page into categorised sections.
//
// The owner writes the page in the Ghost editor with built-in pieces only
// (page-from-our-home.hbs lists them): a Heading per category, Ghost's
// Product card per product. This script reads that article and arranges
// it -- sections, a filter row, a round photo per section -- and only runs
// where the template has marked the content with .pt-shelf-source.
//
// Everything is built from a clone and swapped in at the end, so a failure
// part-way leaves the original article exactly as Ghost rendered it, and
// the product cards are styled by CSS alone, so that article still reads
// as a shop.

// One accent per section (design-system.md section 1). The four categories
// the page launched with keep fixed colours and icons, matched loosely on
// the heading so "Wellness" or "Pets" still land where they should. A new
// category takes the first accent not already in use. Peach is not in this
// list: it is reserved for the featured quote.
const ICONS = {
    leaf: '<path d="M5 19c0-8 5-13 14-14 0 9-5 14-13 14"/><path d="M5 19l8-8"/>',
    paw: '<circle cx="5.5" cy="10" r="1.8"/><circle cx="9.5" cy="5.8" r="1.8"/><circle cx="14.5" cy="5.8" r="1.8"/><circle cx="18.5" cy="10" r="1.8"/><path d="M12 11.5c-3 0-6 4.6-6 6.7 0 1.6 1.3 2.3 2.7 2.3 1.4 0 2.2-.8 3.3-.8s1.9.8 3.3.8c1.4 0 2.7-.7 2.7-2.3 0-2.1-3-6.7-6-6.7z"/>',
    house: '<path d="M3 11.5 12 4l9 7.5"/><path d="M5.5 9.5V20h13V9.5"/><path d="M10 20v-5h4v5"/>',
    camper: '<path d="M2 16V8a2 2 0 0 1 2-2h11a5 5 0 0 1 5 5v5z"/><circle cx="7.5" cy="17" r="2"/><path d="M20 16h2"/><path d="M6 10h4"/><path d="M14 10h2"/>',
    heart: '<path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z"/>',
};

const KNOWN = [
    { match: /well|health|body|self/i, accent: 'sage', icon: 'leaf' },
    { match: /tillie|dog|pet|pup/i, accent: 'blossom', icon: 'paw' },
    { match: /home|house|table|kitchen/i, accent: 'sky', icon: 'house' },
    { match: /road|rv|camp|travel|trip/i, accent: 'butter', icon: 'camper' },
];

const ACCENTS = ['sage', 'blossom', 'sky', 'butter'];

const isAmazon = (href) => {
    try {
        return /(^|\.)(amazon\.[a-z.]+|amzn\.to|link\.amazon)$/i.test(new URL(href).hostname);
    } catch (e) {
        return false;
    }
};

const el = (tag, className, html) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (html) node.innerHTML = html;
    return node;
};

const icon = (name) => `<svg viewBox="0 0 24 24" aria-hidden="true">${ICONS[name]}</svg>`;

// Affiliate links must say so to search engines (rel=sponsored). Ghost's own
// product button already opens a new tab; links typed into a description
// follow suit, so a reader browsing products keeps their place here.
function markSponsored(a) {
    a.target = '_blank';
    a.rel = isAmazon(a.href) || a.closest('.kg-product-card-button, .pt-shelf-callout') ? 'sponsored noopener' : 'noopener';
}

// A link whose text is the URL itself ("https://brightsideanimals.org")
// was pasted, not written. Show the site's name instead of the address.
function shortenRawLinks(root) {
    root.querySelectorAll('a').forEach((a) => {
        if (!/^(https?:\/\/|www\.)\S+$/i.test(a.textContent.trim())) return;
        try {
            a.textContent = new URL(a.href).hostname.replace(/^www\./, '');
        } catch (e) { /* leave an unparseable link as written */ }
    });
}

const isImageFigure = (node) => node.tagName === 'FIGURE' && node.classList.contains('kg-image-card');
const isProduct = (node) => node.classList.contains('kg-product-card');
const isEmpty = (node) => node.tagName === 'P' && !node.textContent.trim() && !node.querySelector('img');

// Split the article at its headings. Everything before the first heading is
// the owner's letter; each heading owns what follows it until the next.
function readSections(source) {
    const intro = [];
    const sections = [];
    let current = null;

    [...source.children].forEach((node) => {
        if (isEmpty(node) || node.tagName === 'HR') return;
        if (node.tagName === 'H2') {
            current = { heading: node, nodes: [] };
            sections.push(current);
        } else if (current) {
            current.nodes.push(node);
        } else {
            intro.push(node);
        }
    });

    return { intro, sections };
}

function assignStyles(sections) {
    const used = new Set();
    sections.forEach((s) => {
        const known = KNOWN.find((k) => k.match.test(s.heading.textContent));
        if (known && !used.has(known.accent)) {
            s.accent = known.accent;
            s.icon = known.icon;
            used.add(known.accent);
        }
    });
    sections.forEach((s, i) => {
        if (s.accent) return;
        s.accent = ACCENTS.find((a) => !used.has(a)) || ACCENTS[i % ACCENTS.length];
        s.icon = 'heart';
        used.add(s.accent);
    });
}

function callout(quote) {
    const box = el('aside', 'pt-shelf-callout');
    const links = quote.querySelectorAll('a');
    const cta = links[links.length - 1];

    if (cta) {
        const wrapper = cta.parentElement;
        if (wrapper !== quote && wrapper.matches('strong, b') && wrapper.textContent.trim() === cta.textContent.trim()) {
            wrapper.replaceWith(cta);
        }
        cta.remove();
    }

    const text = el('p');
    text.append(...quote.childNodes);
    box.append(text);

    if (cta) {
        cta.className = 'gh-button gh-button-large';
        cta.textContent = cta.textContent.trim();
        const actions = el('div', 'pt-shelf-callout-actions');
        actions.append(cta);
        box.append(actions);
    }
    return box;
}

function buildSection(s) {
    const section = el('section', `pt-shelf-section accent-${s.accent}`);
    section.dataset.category = s.heading.id;
    section.setAttribute('aria-labelledby', s.heading.id);

    // A photo placed straight under the heading is the section's face
    // (Tillie, for her section); otherwise the category's icon.
    let rest = s.nodes;
    let mark;
    if (rest[0] && isImageFigure(rest[0])) {
        const img = rest[0].querySelector('img');
        mark = el('div', 'pt-shelf-mark is-photo');
        mark.append(img);
        rest = rest.slice(1);
    } else {
        mark = el('div', 'pt-shelf-mark', icon(s.icon));
    }

    const head = el('header', 'pt-shelf-head');
    const titles = el('div', 'pt-shelf-titles');
    titles.append(s.heading);
    if (rest[0] && rest[0].tagName === 'P') {
        rest[0].classList.add('pt-shelf-desc');
        titles.append(rest[0]);
        rest = rest.slice(1);
    }
    head.append(mark, titles);
    section.append(head);

    // Keep the owner's order. Consecutive products share a grid; anything
    // else between them (a paragraph, a quote, an image) breaks the run.
    let grid = null;
    let prose = null;
    rest.forEach((node) => {
        if (isProduct(node)) {
            prose = null;
            if (!grid) {
                grid = el('div', 'pt-shelf-grid');
                section.append(grid);
            }
            grid.append(node);
            return;
        }
        grid = null;
        if (node.tagName === 'P') {
            if (!prose) {
                prose = el('div', 'pt-shelf-prose');
                section.append(prose);
            }
            prose.append(node);
            return;
        }
        prose = null;
        section.append(node.tagName === 'BLOCKQUOTE' ? callout(node) : node);
    });

    s.count = section.querySelectorAll('.kg-product-card').length;
    return section;
}

function buildFilter(sections, onPick) {
    const bar = el('div', 'pt-shelf-filter');
    const group = el('div', 'pt-shelf-chips');
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', 'Filter by category');

    const total = sections.reduce((n, s) => n + s.count, 0);
    const chips = [{ id: '', label: 'All picks', count: total }].concat(
        sections.map((s) => ({ id: s.heading.id, label: s.heading.textContent.trim(), count: s.count, accent: s.accent })),
    );

    chips.forEach((c) => {
        const button = el('button', 'pt-shelf-chip');
        button.type = 'button';
        button.dataset.category = c.id;
        if (c.accent) button.append(el('span', `pt-shelf-dot accent-${c.accent}`));
        const label = el('span');
        label.textContent = c.label;
        const count = el('span', 'pt-shelf-count');
        count.textContent = c.count;
        button.append(label, count);
        button.addEventListener('click', () => onPick(c.id));
        group.append(button);
    });

    bar.append(group);
    return bar;
}

function build(source) {
    const { intro, sections } = readSections(source);

    // No headings yet: there is nothing to file products under, so leave
    // the article as Ghost rendered it -- the product cards are styled by
    // CSS alone and still read as a shop.
    if (!sections.length) return null;

    const shelf = el('div', 'pt-shelf');

    if (intro.length) {
        const letter = el('div', 'pt-shelf-letter');
        letter.append(...intro);
        shelf.append(letter);
    }

    assignStyles(sections);
    const built = sections.map(buildSection);

    const show = (id) => {
        built.forEach((section) => { section.hidden = Boolean(id) && section.dataset.category !== id; });
        shelf.querySelectorAll('.pt-shelf-chip').forEach((chip) => {
            chip.setAttribute('aria-pressed', String(chip.dataset.category === id));
        });
    };

    // A category can be shared as a link: /from-our-home/#tillie-approved.
    // The address is only rewritten on a click -- on load the hash may be
    // Ghost's own (#/portal/signup), and wiping it would break sign-up links.
    const pick = (id) => {
        show(id);
        history.replaceState(null, '', id ? `#${id}` : location.pathname + location.search);
    };

    shelf.append(buildFilter(sections, pick), ...built);
    shelf.querySelectorAll('a').forEach(markSponsored);
    shortenRawLinks(shelf);

    const fromHash = decodeURIComponent(location.hash.slice(1));
    shelf.showInitial = () => show(sections.some((s) => s.heading.id === fromHash) ? fromHash : '');
    return shelf;
}

// The site header is sticky, so the filter row has to stick just below it.
function trackHeaderHeight() {
    const head = document.querySelector('.gh-head');
    if (!head) return;
    const set = () => document.documentElement.style.setProperty('--pt-head-h', `${head.offsetHeight}px`);
    set();
    window.addEventListener('resize', set);
}

export default function shelf() {
    const source = document.querySelector('.pt-shelf-source');
    if (!source) return;

    try {
        const built = build(source.cloneNode(true));
        if (built) {
            source.replaceWith(built);
            built.showInitial();
            trackHeaderHeight();
        }
    } catch (e) {
        // The plain article is still in place; just make sure it shows.
        console.error('shelf:', e);
    } finally {
        document.documentElement.classList.remove('pt-shelf-pending');
    }
}
